import { createFileRoute } from '@tanstack/react-router'
import { Link, useTranslation, useSelector, useDispatch } from '@spcc/core'
import { getNextLanguage, isRtlLanguage } from '@spcc/portal-shared'
import { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Button,
  Label,
  ArrowLeft,
  Globe,
  Mail,
  CheckCircle2,
  Alert,
  AlertDescription,
  AlertTriangle,
} from '@spcc/uikit'
import {
  authActions,
  selectIsPasswordResetLoadingState,
  selectPasswordResetStatus,
  selectPasswordResetMessage,
} from '@spcc/domains/auth'

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
})

function submitForgotPassword(dispatch: ReturnType<typeof useDispatch>, email: string): void {
  dispatch(authActions.forgotPassword({
    email: email.trim(),
  }))
}

function resetForgotPasswordState(dispatch: ReturnType<typeof useDispatch>, setEmail: (value: string) => void): void {
  dispatch(authActions.resetPasswordReset())
  setEmail('')
}

function isPasswordResetSuccessful(status: string): boolean {
  return status === 'success'
}

function shouldShowPasswordResetFailureAlert(status: string): boolean {
  return status === 'error'
}

function ForgotPasswordPage() {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()

  const isLoading = useSelector(selectIsPasswordResetLoadingState)
  const passwordResetStatus = useSelector(selectPasswordResetStatus)
  const passwordResetMessage = useSelector(selectPasswordResetMessage)

  const [email, setEmail] = useState('')

  const isRTL = isRtlLanguage(i18n.language)
  const isSubmitted = isPasswordResetSuccessful(passwordResetStatus)
  const showResetFailureAlert = shouldShowPasswordResetFailureAlert(passwordResetStatus)

  useEffect(() => {
    dispatch(authActions.resetPasswordReset())

    return () => {
      dispatch(authActions.resetPasswordReset())
    }
  }, [dispatch])

  const toggleLanguage = () => {
    const newLang = getNextLanguage(i18n.language)
    i18n.changeLanguage(newLang)
    document.documentElement.dir = isRtlLanguage(newLang) ? 'rtl' : 'ltr'
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    submitForgotPassword(dispatch, email)
  }

  const handleTryAgain = () => {
    resetForgotPasswordState(dispatch, setEmail)
  }

  return (
    <div className="safe-top relative min-h-screen flex items-center justify-center overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary/30 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-accent/20 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="absolute top-[calc(env(safe-area-inset-top)+1rem)] end-4 glass"
        onClick={toggleLanguage}
      >
        <Globe className="h-5 w-5" />
      </Button>

      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <span className="text-2xl font-bold text-primary">South Gate</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('auth.resetPassword')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('auth.resetSubtitle')}
          </p>
        </div>

        <Card className="glass-card border-white/10">
          {!isSubmitted ? (
            <>
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-xl">{t('auth.forgotPassword')}</CardTitle>
                <CardDescription>
                  {t('auth.resetInstructions')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('auth.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder={t('auth.emailPlaceholder')}
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        required
                        className="bg-background/50 pl-10 rtl:pl-3 rtl:pr-10"
                      />
                    </div>
                  </div>

                  {showResetFailureAlert && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{t('auth.passwordResetError')}</AlertDescription>
                    </Alert>
                  )}

                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : (
                      t('auth.sendResetLink')
                    )}
                  </Button>
                </form>

                <div className="mt-6">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm text-primary hover:underline"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1 rtl:rotate-180" />
                    {t('auth.backToLogin')}
                  </Link>
                </div>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="space-y-1 pb-4">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle2 className="h-8 w-8 text-green-500" />
                  </div>
                </div>
                <CardTitle className="text-xl text-center">{t('auth.checkEmail')}</CardTitle>
                <CardDescription className="text-center">
                  {passwordResetMessage || t('auth.resetEmailSentMessage', { email })}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-center text-muted-foreground">
                  {t('auth.didntReceiveEmail')}
                </p>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleTryAgain}
                >
                  {t('auth.tryAgain')}
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm text-primary hover:underline"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1 rtl:rotate-180" />
                    {t('auth.backToLogin')}
                  </Link>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  )
}
